import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  COLOR,
  DateTime,
  Form,
  getColorClassName,
  Heading,
  Paragraph,
  Row,
  Selectable,
  SelectableItem,
  Separator,
  StructuredInfo,
  StructuredInfoItem,
  ToggleButton,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import {
  IndividualForOfferDTO,
  SetSignersRq,
} from "@smartx/mikro-vt-client-api"
import { useFormik } from "formik"
import { useCallback, useContext } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import * as Yup from "yup"
import { offerSetSignersApi } from "../../api/apiClient"
import { useOfferData } from "../../app/hooks"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import AuthContext from "../../providers/AuthProvider"
import { RequestHeader } from "../../utils/utils"
import {
  OfferActionButton,
  OfferNavButtons,
  OfferSaveButton,
} from "./OfferNavButtons"
import { useSaveModal } from "./OfferModalContext"
import { OFFER_STEPS } from "./OfferStepper"

interface IOfferSignersProps {
  signers: IndividualForOfferDTO[]
  isCompact?: boolean
}

interface IExtendedIndividualForOffer extends IndividualForOfferDTO {
  id: string
}

const OfferSignersComponent: React.FC<IOfferSignersProps> = ({
  signers,
  isCompact,
}) => {
  const authContext = useContext(AuthContext)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { goToNextOfferScreen } = useOfferScreenFlow()
  const { openErrorModal } = useSaveModal()
  const offerData = useOfferData()
  const signerData: IExtendedIndividualForOffer[] = signers?.map((el, idx) => ({
    ...el,
    id: `${idx}`?.padStart(2, "0"),
  }))
  const showEditButton = isCompact && signers.length > 1

  const formik = useFormik({
    initialValues: {
      selectedSigners: signerData
        ?.filter((el) => el.signer)
        ?.map((el) => el.id),
    },
    validationSchema: Yup.object({
      selectedSigners: Yup.array()
        .min(1, t("offers.signers.warning.min"))
        .max(2, t("offers.signers.warning.max"))
        .test("independent", t("offers.signers.warning.min"), (values) => {
          const selectedItems = signerData?.filter((el) =>
            values?.includes(el.id),
          )
          return (
            selectedItems.some(
              (el) => el.optenRepresentationMode === "önálló",
            ) || selectedItems.length >= 2
          )
        }),
    }),
    onSubmit: () => handleSubmit(),
  })

  const saveButtonHandler = () => {
    save(false)
  }

  const handleSubmit = () => {
    save(true)
  }

  const save = async (nextStep: boolean) => {
    const setNextStep = offerData.offerState === "SIGNERS" ? nextStep : false

    authContext.refresh()

    const rq: SetSignersRq = {
      header: new RequestHeader(),
      caseId: offerData?.caseId,
      signers: signerData
        ?.filter((el) => formik.values.selectedSigners.includes(el.id))
        ?.map((i) => ({
          birthDate: i.birthDate,
          name: i.name,
        })),
      nextStep: setNextStep,
    }

    try {
      const result = await offerSetSignersApi.setSigners(rq)
      if (!result?.data?.success) {
        throw new Error("Signers error")
      }

      if (nextStep) {
        goToNextOfferScreen(offerData?.caseId)
      } else {
        navigate("/dashboard", { replace: true })
      }
    } catch {
      openErrorModal(() => goToNextOfferScreen(offerData?.caseId))
    }
  }

  const getSignerName = useCallback(
    (signer: any) => {
      return (
        <span
          className={
            isCompact
              ? getColorClassName(COLOR.BLUE_400, { foreground: true })
              : ""
          }
        >
          {signer.name}
        </span>
      )
    },
    [isCompact],
  )

  const getSignerInfo = useCallback((signer: any) => {
    return (
      <>
        {signer.optenPersonPositionType},{" "}
        <DateTime date={new Date(signer.birthDate)} format="yyyy.MM.dd." />
      </>
    )
  }, [])

  const headerContent = <Heading level={1}>{t("offers.signers.title")}</Heading>

  const textContent = (
    <Paragraph className="mb-3">{t("offers.signers.text")}</Paragraph>
  )

  const editButtonContent = (
    <OfferActionButton
      actionButtonText={t("offers.summary.button.edit")}
      icon={Button.ICON.EDIT}
      onActionButtonClicked={() =>
        navigate(`/offer/${OFFER_STEPS.Signers}`, { replace: true })
      }
      className="mt-3"
    />
  )

  const signerListContentXs = (
    <Card nested={false}>
      <Selectable
        type={Selectable.TYPE.CHECKBOX}
        verticalAlign={Selectable.VERTICAL_ALIGN.MIDDLE}
        selectOnClick={true}
        selectedItems={formik.values.selectedSigners}
        onSelect={(selected) =>
          formik.setFieldValue("selectedSigners", selected)
        }
      >
        {signerData?.map((el, idx) => (
          <>
            <Card key={`signer-${el.id}`}>
              <SelectableItem value={el.id} disabled={isCompact}>
                <StructuredInfo
                  horizontal
                  header={
                    <>
                      <StructuredInfoItem>
                        {getSignerName(el)}
                      </StructuredInfoItem>
                      <StructuredInfoItem
                        faded
                        size={StructuredInfoItem.SIZE.SMALL}
                      >
                        {getSignerInfo(el)}
                      </StructuredInfoItem>
                    </>
                  }
                />
              </SelectableItem>
            </Card>
            {idx < signerData.length - 1 && <Separator />}
          </>
        ))}

        {showEditButton && <Card>{editButtonContent}</Card>}
      </Selectable>
    </Card>
  )

  const addSigner = (index: string) => {
    formik.setFieldValue("selectedSigners", [
      ...formik.values.selectedSigners,
      index,
    ])
  }

  const removeSigner = (index: string) => {
    formik.setFieldValue(
      "selectedSigners",
      formik.values.selectedSigners.filter((el) => el !== index),
    )
  }

  const signerListContent = (
    <>
      <Row>
        {signerData?.map((el) => (
          <Col md={4} key={`signer-${el.id}`}>
            <ToggleButton
              description={getSignerInfo(el)}
              label={getSignerName(el)}
              name={`user-${el.id}`}
              value={el.id}
              toggled={formik.values.selectedSigners.includes(el.id)}
              onToggle={(toggled) =>
                toggled ? addSigner(el.id) : removeSigner(el.id)
              }
              disabled={isCompact}
              className="signer-toggle-box"
            />
          </Col>
        ))}
      </Row>

      {showEditButton && (
        <ButtonGroup className="text-center">{editButtonContent}</ButtonGroup>
      )}
    </>
  )

  const navButtonsContent = <OfferNavButtons />

  const saveButtonContent = (
    <OfferSaveButton onSaveButtonClicked={saveButtonHandler} />
  )

  const warning = (
    <>
      {!formik.isValid && !!formik.submitCount && (
        <Alert heading="" variant={Alert.VARIANT.WARNING}>
          {formik.errors.selectedSigners?.toString()}
        </Alert>
      )}
    </>
  )

  const pageContent = useMatchMediaTierProp({
    xs: (
      <>
        {isCompact && signerListContentXs}

        {!isCompact && (
          <>
            {headerContent}

            {textContent}

            {signerListContentXs}

            {warning}

            {navButtonsContent}
          </>
        )}
      </>
    ),
    md: (
      <>
        {isCompact && signerListContent}

        {!isCompact && (
          <Card nested={false}>
            <CardBody>
              {headerContent}

              {textContent}

              {signerListContent}

              {warning}
            </CardBody>
            <Separator />
            <CardBody>{navButtonsContent}</CardBody>
          </Card>
        )}
      </>
    ),
  })

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        formik.handleSubmit()
      }}
    >
      {pageContent}

      {!isCompact && saveButtonContent}
    </Form>
  )
}

const OfferSignersPage = () => {
  const offerData = useOfferData()

  return <OfferSignersComponent signers={offerData?.individuals} />
}

export { OfferSignersComponent, OfferSignersPage }
